import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { PaperPlaneTilt, CircleNotch, CheckCircle, WarningCircle, UserCircle } from '@phosphor-icons/react';
import { useAuthOptional } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../i18n-review/client/Msg';

export interface FeedbackFormSuccessDetail {
  authenticated: boolean;
}

interface FeedbackFormProps {
  source?: string;
  metadata?: Record<string, unknown>;
  onSuccess?: (detail?: FeedbackFormSuccessDetail) => void;
  onCancel?: () => void;
  className?: string;
}

export function FeedbackForm({ source = 'general', metadata, onSuccess, onCancel, className = '' }: FeedbackFormProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const auth = useAuthOptional();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const signIn = auth?.signIn;
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage(null);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: feedback.trim(),
          source,
          metadata,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit feedback');
      }

      setSubmitStatus('success');
      setFeedback('');
      trackEvent({ name: 'feedback_submit', params: { locale: intl.locale } });

      if (onSuccess) {
        // When AuthProvider is missing, treat as authenticated so modals don't stay open unnecessarily.
        onSuccess({ authenticated: auth ? isAuthenticated : true });
      }

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred');
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Allow anonymous feedback - no authentication required

  return (
    <form onSubmit={handleSubmit} className={`space-y-5 ${className}`}>
      <div>
        <label htmlFor="feedback-message" className="block text-sm font-medium text-[#3D3529] dark:text-[#C4A882] mb-3">
          <Msg
            id="feedback.label"
            defaultMessage="Your Feedback"
            description="Label for feedback textarea"
          />
        </label>
        <textarea
          id="feedback-message"
          className="w-full px-4 py-3 border border-[#DDD5C8]/80 dark:border-[#4A3520]/80 rounded-2xl bg-[#F8F5F0]/80 dark:bg-[#170F07]/60 text-[#1E1A14] dark:text-[#F2EADF] placeholder-[#7A6E5F] dark:placeholder-[#8A7060] focus:outline-none focus:ring-2 focus:ring-[#3B5E45]/40 dark:focus:ring-[#98CFA8]/40 focus:border-[#3B5E45]/50 dark:focus:border-[#98CFA8]/50 resize-none transition-all duration-200 cursor-text"
          rows={5}
          placeholder={formatMessage({
            id: 'feedback.placeholder',
            defaultMessage: 'Tell us what we should change or add...',
            description: 'Placeholder text for feedback textarea',
          })}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={isSubmitting}
          required
        />
      </div>

      {submitStatus === 'success' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[#3B5E45] dark:text-[#98CFA8] text-sm px-4 py-3 bg-[#C8DDCE]/50 dark:bg-[#1A2E22]/60 rounded-2xl border border-[#3B5E45]/20 dark:border-[#98CFA8]/20">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <Msg
              id="feedback.success"
              defaultMessage="Thank you! Your feedback has been submitted."
              description="Success message after submitting feedback"
            />
          </div>
          {!isAuthenticated && signIn ? (
            <div className="rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-[#F8F5F0]/80 dark:bg-[#170F07]/60 px-4 py-4 space-y-3">
              <p className="text-sm font-medium text-[#1E1A14] dark:text-[#F2EADF]">
                <Msg
                  id="feedback.postSubmit.signInTitle"
                  defaultMessage="Want us to follow up?"
                  description="Heading after anonymous feedback submit"
                />
              </p>
              <p className="text-xs text-[#5A4A3A] dark:text-[#B8A48C] leading-relaxed">
                <Msg
                  id="feedback.postSubmit.signInBody"
                  defaultMessage="Activate your account so we can reply if we have questions about your feedback."
                  description="Body after anonymous feedback submit encouraging sign-in"
                />
              </p>
              <button
                type="button"
                onClick={() => {
                  trackEvent({
                    name: 'auth_interaction',
                    params: { action: 'sign_in_click', surface: 'feedback_post_submit', locale: intl.locale },
                  });
                  signIn();
                }}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#3B5E45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2F4A38] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#98CFA8] cursor-pointer"
              >
                <UserCircle className="h-4 w-4 shrink-0" />
                <Msg
                  id="feedback.postSubmit.signInButton"
                  defaultMessage="Sign in or activate account"
                  description="Button after feedback submit to sign in"
                />
              </button>
            </div>
          ) : null}
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-2xl">
          <WarningCircle className="w-4 h-4 flex-shrink-0" />
          <span>
            {errorMessage || formatMessage({
              id: 'feedback.error',
              defaultMessage: 'Failed to submit feedback. Please try again.',
              description: 'Error message when feedback submission fails',
            })}
          </span>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-sm font-medium text-[#3D3529] dark:text-[#C4A882] hover:bg-[#F2EADF] dark:hover:bg-[#2A1D0F] rounded-2xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3B5E45]/30 dark:focus:ring-[#98CFA8]/30 cursor-pointer"
          >
            <Msg
              id="feedback.cancel"
              defaultMessage="Cancel"
              description="Cancel button text"
            />
          </button>
        )}
        <button
          type="submit"
          disabled={!feedback.trim() || isSubmitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B5E45] hover:bg-[#2F4A38] disabled:bg-[#DDD5C8] dark:disabled:bg-[#4A3520] disabled:cursor-not-allowed text-white text-sm font-medium rounded-2xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#98CFA8]/50 focus:ring-offset-2 focus:ring-offset-[#F8F5F0] dark:focus:ring-offset-[#170F07] shadow-sm hover:shadow-md dark:hover:shadow-none disabled:shadow-none cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <CircleNotch className="w-4 h-4 animate-spin" />
              <Msg
                id="feedback.submitting"
                defaultMessage="Submitting..."
                description="Button text when submitting feedback"
              />
            </>
          ) : (
            <>
              <PaperPlaneTilt className="w-4 h-4" />
              <Msg
                id="feedback.submit"
                defaultMessage="Submit"
                description="Button text for submitting feedback"
              />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
